import { InfoIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { componentKey } from '@entities/search-session';
import { ScoreRing } from '@shared/ui';

import { confidenceCategory, confidenceKey, shownMatchCategoryKey, shownMatchScore } from './lib';
import styles from './MatchInsights.module.css';
import type { MatchInfo, ScoreComponent } from './types';

interface MatchSummaryProps {
  match: MatchInfo;
  /** Компоненты, исключённые из модели для этого автомобиля. */
  inapplicable: readonly ScoreComponent[];
  /** `undefined` — разбора уверенности у прогона нет, кнопки не будет. */
  onOpenConfidence?: (() => void) | undefined;
}

/**
 * Шапка блока «Почему автомобиль подходит» (FE-083, MVP_PRD §18).
 *
 * Показывает три разных числа, которые нельзя смешивать:
 *
 * * **Match Score** — соответствие запросу, не оценка техсостояния;
 * * **уверенность в соответствии** — качество данных плюс подробность запроса,
 *   не вероятность удачной покупки (MVP_PRD §17);
 * * **доля применённой модели** — какая часть оценки посчитана. Она показывается
 *   отдельно, чтобы «плохое объявление» и «нечем оценить сценарий» не
 *   маскировали друг друга (MATCHING §8.4), и перечисляет непосчитанные
 *   компоненты поимённо.
 */
export function MatchSummary({ match, inapplicable, onOpenConfidence }: MatchSummaryProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const category = match.match_confidence_category || confidenceCategory(match.match_confidence);
  const coverage = Math.round(match.scoring_coverage);

  return (
    <section className={styles.summary} aria-labelledby={titleId}>
      <div className={styles.summaryHead}>
        <div>
          <h2 id={titleId} className={styles.summaryTitle}>
            {t('results.detail.title')}
          </h2>
          <p className={styles.summaryLead}>{t('results.detail.lead')}</p>
        </div>
        <ScoreRing
          score={shownMatchScore(match.score)}
          size={92}
          title={t('results.card.matchTitle')}
          label={t(shownMatchCategoryKey(match.score))}
        />
      </div>

      <dl className={styles.metrics}>
        <div className={styles.metric}>
          <dt>{t('results.card.confidence')}</dt>
          <dd>
            <span className={`${styles.metricValue} tabular`}>
              {Math.round(match.match_confidence)}
            </span>
            <span className={styles.metricNote}>{t(confidenceKey(category))}</span>
          </dd>
        </div>
        <div className={styles.metric}>
          <dt>{t('results.detail.dataConfidence')}</dt>
          <dd>
            <span className={`${styles.metricValue} tabular`}>
              {Math.round(match.data_confidence)}
            </span>
            <span className={styles.metricNote}>
              {t(confidenceKey(confidenceCategory(match.data_confidence)))}
            </span>
          </dd>
        </div>
        <div className={styles.metric}>
          <dt>{t('results.detail.coverage')}</dt>
          <dd>
            <span className={`${styles.metricValue} tabular`}>{coverage} %</span>
            <span className={styles.metricNote}>
              {coverage === 100
                ? t('results.detail.coverageFull')
                : t('results.detail.coveragePartial')}
            </span>
          </dd>
        </div>
      </dl>

      {inapplicable.length > 0 ? (
        <p className={styles.inapplicable}>
          {t('results.detail.inapplicable', {
            components: inapplicable.map((code) => t(componentKey(code))).join(', '),
          })}
        </p>
      ) : null}

      {onOpenConfidence ? (
        <button type="button" className={styles.howButton} onClick={onOpenConfidence}>
          <InfoIcon size={16} weight="fill" aria-hidden="true" />
          {t('results.detail.how')}
        </button>
      ) : null}
    </section>
  );
}
